import React, { FC, useCallback, useMemo } from "react";
import { IBundleDashboard } from "store/reducers/bundlePromotion.reducers";
import { useStyles } from "../../Dashboard/Dashboard.style";
import { Badge } from "custom";
import { isDate } from "date-fns";
import { format } from "date-fns-tz";
import { useDispatch } from "react-redux";
import {
  getBundleCampaignById,
  updateBundleCampaignStatus,
} from "store/actions/bundlePromotion.actions";
import { setLocation } from "store/actions/auth.actions";
import {
  useApiInfo,
  useBadgeInfo,
  usePermissions,
  useTimeZoneSupport,
} from "hooks";
import { Box, Chip, SplitButton, Text, Tooltip } from "common";
import DateRange from "@material-ui/icons/DateRange";
import AlarmsOn from "@material-ui/icons/AlarmOn";
import InfoIcon from "@material-ui/icons/Info";
import { BUNDLE_PROMOTION_UPDATE_STATUS } from "store/store.types";
import { ECampaignStatus } from "store/store.interface";

export interface IProps {
  promotion: IBundleDashboard;
  listing: any;
}

const OverFlowText: React.FC<any> = ({ text, ...rest }) => {
  return (
    <Tooltip title={text}>
      <Text {...rest}>{text}</Text>
    </Tooltip>
  );
};

const BundlePromotionItem: FC<IProps> = ({ promotion, listing }) => {
  const { type, status, startDate, endDate, description, name, campaignId } =
    promotion;

  const { isLoading } = useApiInfo(BUNDLE_PROMOTION_UPDATE_STATUS);
  const bundleStatus = status?.toLowerCase() as ECampaignStatus;

  const dispatch = useDispatch();

  const onApprove = useCallback(() => {
    dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.approved));
  }, [campaignId, dispatch]);

  const onComplete = useCallback(() => {
    dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.completed));
  }, [campaignId, dispatch]);

  const onReject = useCallback(() => {
    dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.rejected));
  }, [campaignId, dispatch]);

  const onView = useCallback(() => {
    dispatch(getBundleCampaignById(campaignId));
    dispatch(setLocation("/cart-bundle-details"));
  }, [campaignId, dispatch]);

  const onPause = useCallback(() => {
    dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.paused));
  }, [campaignId, dispatch]);

  const onTakeLive = useCallback(() => {
    dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.active));
  }, [campaignId, dispatch]);

  const availableUserActions = usePermissions(bundleStatus).filter(
    (action) => !["Get Report", "Download Coupons"].includes(action.title)
  );

  const { dateToTimeZone, timeZone } = useTimeZoneSupport();

  const options = useMemo(() => {
    if (availableUserActions && availableUserActions.length > 0) {
      const availableActions = availableUserActions.map(({ action, title }) => {
        switch (action) {
          case "view":
            return {
              title,
              onClick: onView,
            };
          case "approve":
            return {
              title,
              onClick: onApprove,
            };
          case "reject":
            return {
              title,
              onClick: onReject,
            };
          case "complete":
            return {
              title,
              onClick: onComplete,
            };
          case "pause":
            return {
              title,
              onClick: onPause,
            };
          case "takeLive":
            return {
              title,
              onClick: onTakeLive,
            };
          default:
            return {
              title: "",
              onClick: onView,
            };
        }
      });
      return availableActions;
    }
    return [];
  }, [
    availableUserActions,
    onApprove,
    onReject,
    onView,
    onComplete,
    onPause,
    onTakeLive,
  ]);

  const convertedStartDate = dateToTimeZone(startDate);
  const convertedEndDate = dateToTimeZone(endDate);
  const [formatedStartDate, formatedEndDate] = useMemo(
    () => [
      isDate(convertedStartDate)
        ? format(
            convertedStartDate as Date,
            "MMMM d','yyyy 'at' hh:mm:ss' 'aa XX",
            { timeZone }
          )
        : "",
      isDate(convertedEndDate)
        ? format(
            convertedEndDate as Date,
            "MMMM d','yyyy 'at' hh:mm:ss' 'aa XX",
            { timeZone }
          )
        : "",
    ],
    [convertedEndDate, convertedStartDate, timeZone]
  );

  const { bgColor } = useBadgeInfo(bundleStatus);
  const { item, icon, dateText, wrapText, overFlow } = useStyles({
    color: bgColor,
    listing,
  });

  return (
    <Box
      className={item}
      display="flex"
      flexDirection="column"
      alignItems="center"
      data-testid="bundle-promotion-item-container"
    >
      <Box padding="20px" width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={["flex-start", "center", "center", "center"]}
          flexDirection={["column", "row", "row", "row"]}
          marginY="5px"
          width="100%"
        >
          <Box
            display="flex"
            alignItems={["flex-start", "center", "center", "center"]}
            flexDirection={["column-reverse", "row", "row", "row"]}
            mb={["15px", "0px", "0px", "0px"]}
            flexWrap="wrap"
          >
            <Chip
              data-testid="role-sticker-type"
              label={type}
              customColor="#90A0B7"
              backgroundColor="#fff"
              fontSize="12px"
              width="auto"
              height="26px"
              margin="0 10px 7px 0"
              padding="5px 20px"
              borderRadius="5px"
              border={`1px solid #90A0B7`}
            />
            <OverFlowText
              className={wrapText}
              text={name}
              fontSize="16px"
              mb="7px"
              fontWeight={600}
            />
          </Box>
          <Badge
            as={bundleStatus}
            margin="0px 0px 7px 0px"
            variant="contained"
            borderRadius="5px"
            width="120px"
            height="26px"
          />
        </Box>
        {description && (
          <Box display="flex" width="100%" alignItems="center" marginY="5px">
            <InfoIcon color="primary" className={icon} />
            <Text marginLeft="10px" className={overFlow}>
              {description}
            </Text>
          </Box>
        )}
        <Box marginY="5px" display="flex" width="100%" alignItems="center">
          <DateRange color="primary" className={icon} />
          <Box
            marginLeft="10px"
            display="flex"
            alignItems="center"
            flexDirection="column"
          >
            <Text fontWeight={600} fontSize="13px" className={dateText}>
              <span>Start on</span>
              {formatedStartDate}
            </Text>
          </Box>
        </Box>
        <Box my="5px" display="flex">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            alignContent="center"
          >
            <AlarmsOn color="primary" className={icon} />
            <Box
              marginLeft="10px"
              display="flex"
              alignItems="center"
              flexDirection="column"
            >
              <Text className={dateText} fontWeight={600} fontSize="13px">
                <span>End on</span>
                {formatedEndDate}
              </Text>
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={["flex-start", "center", "center", "center"]}
          flexDirection={["column", "row", "row", "row"]}
          width="100%"
          mt="5px"
        >
          <Box
            display="flex"
            alignItems="center"
            alignContent="center"
            flexDirection="row"
          >
            {![ECampaignStatus.active, ECampaignStatus.completed].includes(
              bundleStatus
            ) && (
              <Text
                color="text.secondary"
                fontSize="13px"
                fontWeight={600}
                data-testid="progress-value-default"
                my={["10px", "0px"]}
              >
                You will get information about the campaign progress when live
              </Text>
            )}
          </Box>
          {options && options.length > 0 && (
            <Box
              display="flex"
              alignSelf={["flex-start", "flex-end", "flex-end", "flex-end"]}
              mt={["10px", "0px", "0px", "0px"]}
            >
              <SplitButton
                options={options}
                isLoading={isLoading}
                testId="group-bundle-buttons"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BundlePromotionItem;
